/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import type { Service } from '@ahm/api-wrappers-core';
import {
  Accordion,
  ActionIcon,
  Box,
  Card,
  Center,
  Group,
  Image,
  Stack,
  Text,
  Title,
  em,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import type { ContextModalProps } from '@mantine/modals';
import { useEffect, useState } from 'react';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import BoxIcon from '@/assets/icons/box-icon.svg';
import IconChevronLeft from '@/assets/icons/truck/chevron-left.svg';
import IconChevronRight from '@/assets/icons/truck/chevron-right.svg';
import WeightIcon from '@/assets/icons/weight-icon.svg';
import { useLocaleSuffix } from '@/navigation';
import { useDetectModalStore } from '@/stores';

export type ServiceDetailsModalProps = {
  service: Service;
  onChangeService?: (s: Service) => void;
};

const ServiceDetailsModal = ({
  innerProps,
  id: modalId,
}: ContextModalProps<ServiceDetailsModalProps>) => {
  const { service } = innerProps;
  const localeSuffix = useLocaleSuffix();
  const isMobile = useMediaQuery(`(max-width: ${em(767)})`);
  const { setDetectModalId, clearDetectModalId } = useDetectModalStore(
    (state) => state
  );

  useEffect(() => {
    setDetectModalId(modalId);
    return () => clearDetectModalId();
  }, [modalId]);

  const vehicleInfoList = [
    {
      id: 'pricing_details',
      label: 'Bảng giá chi tiết',
      content: service?.vehicle_info?.fee_description,
    },

    {
      id: 'truck_ban_hours',
      label: 'Giờ cấm tải',
      content: service?.vehicle_info?.truck_ban_description,
    },

    {
      id: 'cod',
      label: 'Thu hộ COD',
      content: service?.vehicle_info?.cod_description,
    },
  ];

  const [canSlidePrev, setCanSlidePrev] = useState(false);
  const [canSlideNext, setCanSlideNext] = useState(true);
  const SwiperNavigation = () => {
    const swiper = useSwiper();

    const handleSlidePrev = () => {
      swiper.slidePrev();
    };

    const handleSlideNext = () => {
      swiper.slideNext();
    };

    return (
      <>
        <Box
          pos="absolute"
          top="50%"
          left={24}
          style={{
            zIndex: 5,
            transform: 'translateY(-50%)',
          }}
        >
          <ActionIcon
            size={36}
            variant="outline"
            bg="white"
            color="neutral"
            radius="xl"
            style={(theme) => ({
              border: 'none',
              boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
              display: !canSlidePrev ? 'none' : 'flex',
              '&:hover': {
                color: theme.colors.neutral[9],
                backgroundColor: theme.white,
              },
            })}
            onClick={handleSlidePrev}
          >
            <IconChevronLeft size={18} />
          </ActionIcon>
        </Box>

        <Box
          pos="absolute"
          top="50%"
          right={24}
          style={{
            zIndex: 5,
            transform: 'translateY(-50%)',
          }}
        >
          <ActionIcon
            size={36}
            variant="outline"
            bg="white"
            color="neutral"
            radius="xl"
            style={(theme) => ({
              border: 'none',
              boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
              display: !canSlideNext ? 'none' : 'flex',
              '&:hover': {
                color: theme.colors.neutral[9],
                backgroundColor: theme.white,
              },
            })}
            onClick={handleSlideNext}
          >
            <IconChevronRight size={18} />
          </ActionIcon>
        </Box>
      </>
    );
  };

  return (
    <Box style={{ overflow: 'auto' }}>
      <Center mt={4} mih={230}>
        {service?.vehicle_info?.images &&
        service?.vehicle_info?.images?.length > 0 ? (
          <Swiper
            style={{
              overflow: 'hidden',
              position: 'relative',
              paddingTop: 0,
              padding: 24,
              background: 'white',
              ['--swiper-pagination-color' as string]: '#FF652C',
              ['--swiper-pagination-bottom' as string]: '0px',
            }}
            spaceBetween={12}
            slidesPerView={1}
            // onRealIndexChange={(index) => {
            //   setCanSlidePrev(!index.isBeginning);
            //   setCanSlideNext(!index.isEnd);
            // }}
            pagination={{
              dynamicBullets: true,
            }}
            modules={[Pagination]}
            centeredSlides={true}
            onSwiper={(swiper) => {
              // set trạng thái ngay lần đầu mount
              setCanSlidePrev(!swiper.isBeginning);
              setCanSlideNext(!swiper.isEnd);
            }}
            onSlideChange={(swiper) => {
              setCanSlidePrev(!swiper.isBeginning);
              setCanSlideNext(!swiper.isEnd);
            }}
          >
            {service?.vehicle_info?.images?.length > 1 ? (
              <SwiperNavigation />
            ) : null}
            {service?.vehicle_info?.images.map((img, idx) => (
              <SwiperSlide key={idx}>
                <Image src={img} alt={img} w="100%" h="100%" />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <Box
            style={{
              overflow: 'hidden',
              height: 244,
              position: 'relative',
              padding: 24,
              background: 'white',
            }}
          >
            <Image
              src={service.vehicle_info?.image ?? ''}
              alt={'image-default'}
              fill
              objectFit="contain"
            />
          </Box>
        )}
      </Center>

      <Stack bg="white" gap={8} p={isMobile ? 16 : 24}>
        <Title order={5}>{service[`name_${localeSuffix}`]}</Title>
        {service?.vehicle_info && (
          <>
            <Text size={isMobile ? 'sm' : 'md'}>
              {service?.vehicle_info.short_intro_vi}
            </Text>
          </>
        )}
        {service?.vehicle_info && (
          <Group gap={8} align="stretch" w="100%">
            <Card
              withBorder
              p={12}
              radius={12}
              shadow="md"
              w="calc(50% - 4px)"
              styles={{
                root: {
                  borderColor: 'var(--mantine-color-neutral-1)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                },
              }}
            >
              <Stack gap={8} h="100%" justify="space-between">
                <Stack gap={8}>
                  <Box>
                    <WeightIcon size={24} />
                  </Box>
                  <Text
                    size={isMobile ? 'xs' : 'sm'}
                    c="neutral.5"
                    fw="700"
                    textTransform="uppercase"
                  >
                    Khối lượng
                  </Text>
                </Stack>
                <Title order={5}>{service.vehicle_info?.weight}kg</Title>
              </Stack>
            </Card>
            <Card
              withBorder
              p={12}
              radius={12}
              shadow="md"
              w="calc(50% - 4px)"
              styles={{
                root: {
                  borderColor: 'var(--mantine-color-neutral-1)',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                },
              }}
            >
              <Stack gap={8} h="100%" justify="space-between">
                <Stack gap={8}>
                  <Box>
                    <BoxIcon size={24} />
                  </Box>
                  <Text
                    size={isMobile ? 'xs' : 'sm'}
                    c="neutral.5"
                    fw="700"
                    textTransform="uppercase"
                  >
                    Tổng kích thước
                  </Text>
                </Stack>
                <Title order={5} style={{ wordBreak: 'break-word' }}>
                  {service?.vehicle_info.length_in_cm &&
                  service?.vehicle_info.width_in_cm &&
                  service?.vehicle_info.height_in_cm
                    ? `${service?.vehicle_info.length_in_cm}x${service?.vehicle_info.width_in_cm}x${service?.vehicle_info.height_in_cm}cm`
                    : `${service?.vehicle_info.length}x${service?.vehicle_info.width}x${service?.vehicle_info.height}m`}
                </Title>
              </Stack>
            </Card>
          </Group>
        )}
      </Stack>

      <Box mt="sm" bg="white">
        <Accordion
          multiple
          styles={{
            control: {
              paddingLeft: 24,
              paddingRight: 24,
            },
            content: {
              padding: '16px 24px',
            },
          }}
        >
          {vehicleInfoList.length > 0 &&
            vehicleInfoList.map((item) => (
              <Accordion.Item key={item.id} value={item.id}>
                <Accordion.Control>
                  <Title order={5}>{item.label}</Title>
                </Accordion.Control>
                <Accordion.Panel>
                  <Box style={{ whiteSpace: 'pre-line' }}>{item.content}</Box>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
        </Accordion>
      </Box>
    </Box>
  );
};
//

export default ServiceDetailsModal;
